import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import { PICKER_MINUTES } from '../calendar/calendarConstants';
import ScheduleReminderSection, { useScheduleReminders } from '../shared/ScheduleReminderSection';
import BookingNotifyChoice from '../shared/BookingNotifyChoice';

// Round time to nearest picker step interval
const roundToNearestSnap = (date) => {
  const ms = 1000 * 60 * PICKER_MINUTES;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
};

// All half-hour increments from 0.5h to 8.5h for quick scheduling
const DURATION_OPTIONS = [
  { value: 0.5, labelKey: 'calendar.duration.30min' },
  { value: 1, labelKey: 'calendar.duration.1h' },
  { value: 1.5, labelKey: 'calendar.duration.1_5h' },
  { value: 2, labelKey: 'calendar.duration.2h' },
  { value: 2.5, labelKey: 'calendar.duration.2_5h' },
  { value: 3, labelKey: 'calendar.duration.3h' },
  { value: 3.5, labelKey: 'calendar.duration.3_5h' },
  { value: 4, labelKey: 'calendar.duration.4h' },
  { value: 4.5, labelKey: 'calendar.duration.4_5h' },
  { value: 5, labelKey: 'calendar.duration.5h' },
  { value: 5.5, labelKey: 'calendar.duration.5_5h' },
  { value: 6, labelKey: 'calendar.duration.6h' },
  { value: 6.5, labelKey: 'calendar.duration.6_5h' },
  { value: 7, labelKey: 'calendar.duration.7h' },
  { value: 7.5, labelKey: 'calendar.duration.7_5h' },
  { value: 8, labelKey: 'calendar.duration.8h' },
  { value: 8.5, labelKey: 'calendar.duration.8_5h' },
];

function ScheduleMaintenanceDialog({
  open,
  handleClose,
  maintenance,
  setMaintenance,
  existingSchedule,
}) {
  const canSchedule = useUserStore((s) => can(s.currentOrganization, 'work_orders.schedule'));
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [serviceBays, setServiceBays] = useState([]);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    startDateTime: roundToNearestSnap(new Date()),
    endDateTime: null, // Used for reschedule mode
    duration: 2, // Used for quick schedule mode
    serviceBayId: '',
    employeeIds: [],
    primaryEmployeeId: null,
  });

  // Customer reminder / notification state (shared section + save helper)
  const reminders = useScheduleReminders({
    open,
    maintenanceId: maintenance?.id || null,
    scheduleId: existingSchedule?.id || null,
  });

  // Fetch employees and service bays
  // Booking-linked work orders: tell the customer about the moved time? Reset on every open.
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  useEffect(() => {
    if (open) setNotifyCustomer(true);
  }, [open, existingSchedule]);

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        setLoadingData(true);
        try {
          const [empRes, bayRes] = await Promise.all([
            axios.get('/api/employees/'),
            axios.get('/api/scheduling/service-bays/'),
          ]);
          setEmployees(empRes.data.filter((e) => e.state === 'active'));
          setServiceBays(bayRes.data.filter((b) => b.is_active));
        } catch (err) {
          console.error('Error fetching data:', err);
        } finally {
          setLoadingData(false);
        }
      };
      fetchData();

      // Initialize form
      if (existingSchedule) {
        // Reschedule mode: use start + end datetime
        const start = new Date(existingSchedule.start_time);
        const end = new Date(existingSchedule.end_time);

        // This slot's own mechanics; older payloads without employee_ids fall back to
        // the work order team.
        const teamIds = maintenance.employees?.map((e) => e.id) || [];
        const existingEmployeeIds = Array.isArray(existingSchedule.employee_ids)
          ? [...existingSchedule.employee_ids]
          : teamIds;
        const primaryEmp = maintenance.employees?.find((e) => e.is_primary);
        const primaryId = primaryEmp && existingEmployeeIds.includes(primaryEmp.id)
          ? primaryEmp.id
          : (existingEmployeeIds[0] || null);

        setFormData({
          startDateTime: start,
          endDateTime: end,
          duration: 2, // Not used in reschedule mode
          serviceBayId: existingSchedule.service_bay_id || '',
          employeeIds: existingEmployeeIds,
          primaryEmployeeId: primaryId,
        });
      } else {
        // Quick schedule mode: use duration dropdown
        const existingEmployeeIds = maintenance.employees?.map((e) => e.id) || [];
        const primaryEmp = maintenance.employees?.find((e) => e.is_primary);
        const defaultStart = roundToNearestSnap(new Date());
        const defaultEnd = new Date(defaultStart.getTime() + 2 * 60 * 60 * 1000); // 2 hours later

        setFormData({
          startDateTime: defaultStart,
          endDateTime: defaultEnd,
          duration: 2,
          serviceBayId: '',
          employeeIds: existingEmployeeIds,
          primaryEmployeeId: primaryEmp?.id || existingEmployeeIds[0] || null,
        });
      }
      setError(null);
    }
  }, [open, existingSchedule, maintenance]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData((prev) => {
      const newIds = prev.employeeIds.includes(employeeId)
        ? prev.employeeIds.filter((id) => id !== employeeId)
        : [...prev.employeeIds, employeeId];

      // If we removed the primary employee, set new primary
      let newPrimaryId = prev.primaryEmployeeId;
      if (!newIds.includes(newPrimaryId)) {
        newPrimaryId = newIds[0] || null;
      }

      return { ...prev, employeeIds: newIds, primaryEmployeeId: newPrimaryId };
    });
  };

  const handleSetPrimary = (employeeId) => {
    setFormData((prev) => ({ ...prev, primaryEmployeeId: employeeId }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const startDateTime = formData.startDateTime;
      let endDateTime;

      if (existingSchedule) {
        // Reschedule mode: use the endDateTime directly
        endDateTime = formData.endDateTime;
      } else {
        // Quick schedule mode: calculate end from duration
        endDateTime = new Date(startDateTime);
        endDateTime.setTime(startDateTime.getTime() + formData.duration * 60 * 60 * 1000);
      }

      // Validate that end is after start
      if (endDateTime <= startDateTime) {
        setError(t('End time must be after start time'));
        setLoading(false);
        return;
      }

      if (existingSchedule) {
        // Edit mode: time, bay and this slot's own mechanics. The work order team
        // follows on the backend (scheduling/services/assignment.py).
        await axios.patch(`/api/scheduling/schedules/${existingSchedule.id}/`, {
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          service_bay: formData.serviceBayId || null,
          employee_ids: formData.employeeIds,
          primary_employee_id: formData.primaryEmployeeId,
          ...(maintenance?.has_active_booking ? { notify_customer: notifyCustomer } : {}),
        });
        await reminders.save(existingSchedule.id);
      } else {
        // Create mode: POST to create a new schedule
        const scheduleResponse = await axios.post(`/api/maintenances/${maintenance.id}/schedule/`, {
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          service_bay_id: formData.serviceBayId || null,
          employee_ids: formData.employeeIds.length > 0 ? formData.employeeIds : null,
          primary_employee_id: formData.primaryEmployeeId,
        });
        // The endpoint returns the full maintenance; the newly created schedule
        // is the one with the highest id.
        const createdSchedules = scheduleResponse.data?.schedules || [];
        const newScheduleId = createdSchedules.length
          ? Math.max(...createdSchedules.map((s) => s.id))
          : null;
        await reminders.save(newScheduleId);
      }

      // Refresh maintenance data
      const refreshResponse = await axios.get(`/api/maintenances/${maintenance.id}/`);
      setMaintenance(refreshResponse.data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to schedule maintenance'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 600,
          pb: 1,
        }}
      >
        {existingSchedule ? t('Reschedule maintenance') : t('Quick schedule')}
      </DialogTitle>

      <DialogContent>
        {loadingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                {error}
              </Alert>
            )}

            {/* Linked booking: the slot change syncs it; the checkbox decides about the message */}
            {existingSchedule && maintenance?.has_active_booking && (
              <BookingNotifyChoice
                checked={notifyCustomer}
                onChange={setNotifyCustomer}
                showChoice={
                  !formData.startDateTime ||
                  new Date(existingSchedule.start_time).getTime() !== formData.startDateTime.getTime()
                }
                sx={{ mb: 2 }}
              />
            )}

            {/* Start DateTime */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  mb: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {t('Start time')}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                <DateTimePicker
                  value={formData.startDateTime}
                  onChange={(newValue) => handleFieldChange('startDateTime', newValue)}
                  format="dd.MM.yyyy HH:mm"
                  ampm={false}
                  minutesStep={PICKER_MINUTES}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* Duration/End Time and Service Bay Row */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
              <Box sx={{ flex: 1 }}>
                {existingSchedule ? (
                  // Reschedule mode: End DateTime picker
                  <>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        mb: 1,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}
                    >
                      {t('End time')}
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                      <DateTimePicker
                        value={formData.endDateTime}
                        onChange={(newValue) => handleFieldChange('endDateTime', newValue)}
                        format="dd.MM.yyyy HH:mm"
                        ampm={false}
                        minutesStep={PICKER_MINUTES}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </>
                ) : (
                  // Quick schedule mode: Duration dropdown
                  <>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        mb: 1,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}
                    >
                      {t('Duration')}
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.duration}
                        onChange={(e) => handleFieldChange('duration', e.target.value)}
                        sx={{ borderRadius: '10px' }}
                      >
                        {DURATION_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {t(opt.labelKey)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    mb: 1,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {t('Service bay')}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.serviceBayId}
                    onChange={(e) => handleFieldChange('serviceBayId', e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '10px' }}
                  >
                    <MenuItem value="">
                      <Typography color="text.secondary">{t('Not selected')}</Typography>
                    </MenuItem>
                    {serviceBays.map((bay) => (
                      <MenuItem key={bay.id} value={bay.id}>
                        {bay.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Employees */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  mb: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {t('Assign employees')} <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>({t('Optional')})</Typography>
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: 'text.secondary',
                  mb: 1.5,
                }}
              >
                {t('Click star to set as lead mechanic')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {employees.map((emp) => {
                  const isSelected = formData.employeeIds.includes(emp.id);
                  const isPrimary = formData.primaryEmployeeId === emp.id;

                  return (
                    <Box
                      key={emp.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 1,
                        px: 1.5,
                        borderRadius: '8px',
                        border: 1,
                        borderColor: isSelected ? 'secondary.main' : 'divider',
                        bgcolor: isSelected ? 'rgba(49, 130, 206, 0.08)' : 'transparent',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleEmployeeToggle(emp.id)}
                        size="small"
                        sx={{ p: 0 }}
                      />
                      <Typography
                        onClick={() => handleEmployeeToggle(emp.id)}
                        sx={{
                          fontSize: '13px',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        {emp.name}
                      </Typography>
                      {isSelected && (
                        <Box
                          onClick={() => handleSetPrimary(emp.id)}
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            ml: 0.5,
                          }}
                        >
                          {isPrimary ? (
                            <StarIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                          ) : (
                            <StarBorderIcon
                              sx={{
                                fontSize: 18,
                                color: 'text.disabled',
                                '&:hover': { color: 'warning.main' },
                              }}
                            />
                          )}
                        </Box>
                      )}
                      {isPrimary && isSelected && (
                        <Chip
                          label={t('Lead')}
                          size="small"
                          sx={{
                            height: '18px',
                            fontSize: '10px',
                            fontWeight: 600,
                            bgcolor: 'warning.light',
                            color: 'warning.dark',
                            ml: 0.5,
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
              {employees.length === 0 && (
                <Typography sx={{ fontSize: '13px', color: 'text.disabled', mt: 1 }}>
                  {t('No active employees found')}
                </Typography>
              )}
            </Box>

            <ScheduleReminderSection {...reminders} disabled={loading} />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleCancel}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || loadingData || !canSchedule}
          sx={{
            bgcolor: 'secondary.main',
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
            '&:hover': {
              bgcolor: 'secondary.dark',
            },
          }}
        >
          {loading ? <CircularProgress size={20} /> : <span>{t('Save')}</span>}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ScheduleMaintenanceDialog;
